import { apiRequest } from '../lib/api-client';

/** Data subject requests (LGPD). */

export type SubjectType = 'CONTACT' | 'LEAD';

export interface PrivacyRequest {
  id: string;
  kind: 'EXPORT' | 'ERASURE';
  subject_type: string;
  subject_id: string;
  subject_label: string;
  status: 'COMPLETED' | 'REFUSED';
  reason: string;
  affected: Record<string, number>;
  requested_by: string | null;
  created_at: string;
}

export interface RegistryEntry {
  key: string;
  table: string;
  subjects: SubjectType[];
  policy: 'ERASE' | 'ANONYMISE' | 'KEEP';
  because: string | null;
}

export function listPrivacyRequests(): Promise<{ requests: PrivacyRequest[] }> {
  return apiRequest('/v1/privacy/requests');
}

export function privacyRegistry(): Promise<{
  entries: RegistryEntry[];
  excluded: Record<string, string>;
}> {
  return apiRequest('/v1/privacy/registry');
}

export interface SubjectRef {
  subjectType: SubjectType;
  subjectId?: string;
  email?: string;
  reason: string;
}

export interface SubjectExport {
  subject: { type: SubjectType; id: string; label: string };
  generated_at: string;
  data: Record<string, Record<string, unknown>[]>;
  not_included: Record<string, string>;
}

/**
 * The archive, downloaded.
 *
 * Fetched rather than linked: the endpoint is a POST behind a session, and the archive is never
 * stored anywhere a link could point at — which is the whole design. The file is built here from
 * what came back, handed to the browser, and the object URL released immediately.
 */
export async function downloadSubjectExport(input: SubjectRef): Promise<SubjectExport> {
  const archive = await apiRequest<SubjectExport>('/v1/privacy/export', {
    method: 'POST',
    body: input,
  });

  const blob = new Blob([JSON.stringify(archive, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `dados-${archive.subject.label.replace(/\W+/g, '-').toLowerCase()}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return archive;
}

export function eraseSubject(input: SubjectRef & { confirm: string }): Promise<PrivacyRequest> {
  return apiRequest('/v1/privacy/erase', { method: 'POST', body: input });
}
